/**
 * SortableAccountCard Component
 * 
 * Shared component for displaying account cards with drag-and-drop support.
 * Used in both Accounts.tsx and AccountSettings.tsx.
 */

import { MouseEvent, ReactElement } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  IconButton,
  Chip,
} from "@mui/material";
import CopyFeedbackTooltip from "../../../components/CopyFeedbackTooltip";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Account, AccountGroupDetailField } from "../../../types";

export interface AccountGroup {
  id: string;
  name: string;
  description?: string;
  order: number;
  details?: AccountGroupDetailField[];
}

export interface SortableAccountCardProps {
  account: Account;
  onMenuOpen: (event: MouseEvent<HTMLElement>, account: Account) => void;
  formatCurrency: (amount: number, currency: string) => string;
  accountGroups: AccountGroup[];
  getAccountDetailsForDisplay: (account: Account) => Array<{ name: string; value: string | number }>;
  expandedAccountDetails: Set<string>;
  onToggleDetails: (accountId: string) => void;
  onCopyToClipboard: (text: string, feedbackKey?: string) => void;
  lastCopiedKey?: string | null;
  isMobile?: boolean; // When true, only show "Copied!" tooltip (no "Copy" on mobile)
  valuesVisible?: boolean; // Optional: for Accounts.tsx to hide/show values
  defaultCurrency?: string; // Optional: for Accounts.tsx
  enableOrdering?: boolean; // Optional: show drag handle (Accounts.tsx uses this, AccountSettings.tsx always shows it)
}

export default function SortableAccountCard({
  account,
  onMenuOpen,
  formatCurrency,
  accountGroups,
  getAccountDetailsForDisplay,
  expandedAccountDetails,
  onToggleDetails,
  onCopyToClipboard,
  lastCopiedKey = null,
  isMobile = false,
  valuesVisible = true, // Default to true for AccountSettings.tsx compatibility
  enableOrdering = true, // Default to true for AccountSettings.tsx compatibility
}: SortableAccountCardProps): ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: account.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Determine if drag handle should be shown
  const showDragHandle = enableOrdering;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 4 : 0}
      sx={{
        p: { xs: 1.25, sm: 1 },
        border: 1,
        borderColor: "divider",
        borderRadius: 0.5,
        "&:hover": { boxShadow: showDragHandle ? 2 : 1 },
        transition: "box-shadow 0.2s",
        cursor: showDragHandle ? (isDragging ? "grabbing" : "grab") : "default",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0 }}>
          {showDragHandle && (
            <Box
              {...attributes}
              {...listeners}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: isDragging ? "grabbing" : "grab",
                color: "text.secondary",
                "&:hover": { color: "text.primary" },
                mt: 0.25,
                touchAction: "none", // prevent scroll from stealing drag on mobile
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography 
                variant={showDragHandle ? "body2" : "body1"} 
                fontWeight={showDragHandle ? "bold" : "medium"} 
                sx={{ lineHeight: 1.3 }}
              >
                {account.accountName}
              </Typography>
              {account.details !== undefined && (() => {
                const group = accountGroups.find((g) => g.name === account.group);
                return group?.details && group.details.length > 0;
              })() && (
                <IconButton
                  size="small"
                  onClick={() => onToggleDetails(account.id)}
                  sx={{
                    p: 0.25,
                    minWidth: "auto",
                    width: 24,
                    height: 24,
                    transition: "transform 0.2s",
                    transform: expandedAccountDetails.has(account.id) ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  <ExpandMoreIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              )}
            </Box>
            {account.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: showDragHandle ? 0.25 : 0.5, display: "block", lineHeight: 1.2 }}
              >
                {account.description}
              </Typography>
            )}
            {/* Account Details */}
            {account.details !== undefined && getAccountDetailsForDisplay(account).length > 0 && expandedAccountDetails.has(account.id) && (() => {
              const group = accountGroups.find((g) => g.name === account.group);
              const maxNameLength = group?.details 
                ? Math.max(...group.details.map(d => d.name.length), 10)
                : 10;
              const labelWidth = Math.max(maxNameLength * 8 + 20, 120);
              
              return (
                <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                  {getAccountDetailsForDisplay(account).map((detail) => (
                    <Box
                      key={detail.name}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        ...(detail.value !== "-" && {
                          "&:hover .copy-icon": {
                            opacity: 1,
                          },
                        }),
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ 
                          width: labelWidth,
                          minWidth: labelWidth,
                          fontWeight: 500, 
                          fontSize: "0.875rem",
                          flexShrink: 0
                        }}
                      >
                        {detail.name}:
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          color={detail.value === "-" ? "text.secondary" : "text.primary"}
                          sx={{ fontFamily: "monospace", fontSize: "0.875rem", fontStyle: detail.value === "-" ? "italic" : "normal" }}
                        >
                          {String(detail.value)}
                        </Typography>
                        {detail.value !== "-" && (() => {
                          const copyKey = `${account.id}-${detail.name}`;
                          return (
                            <CopyFeedbackTooltip copyLabel="Copy" feedbackKey={copyKey} lastCopiedKey={lastCopiedKey} isMobile={isMobile}>
                              <IconButton
                                size="small"
                                onClick={() => onCopyToClipboard(String(detail.value), copyKey)}
                                className="copy-icon"
                                sx={{
                                  opacity: 0,
                                  transition: "opacity 0.2s",
                                  p: 0.125,
                                  ml: 0.25,
                                  minWidth: "auto",
                                  width: 20,
                                  height: 20,
                                  flexShrink: 0,
                                  "&:hover": {
                                    opacity: 1,
                                  },
                                }}
                              >
                                <ContentCopyIcon sx={{ fontSize: "0.875rem" }} />
                              </IconButton>
                            </CopyFeedbackTooltip>
                          );
                        })()}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              );
            })()}
          </Box>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          {account.isArchived && (
            <Chip
              label="Archived"
              size="small"
              color="default"
              sx={{ fontSize: "0.7rem", height: 20 }}
            />
          )}
          <Typography
            variant={showDragHandle ? "body2" : "body1"}
            fontWeight="bold"
            color={
              valuesVisible && parseFloat(String(account.amount || 0)) >= 0
                ? "primary.main"
                : valuesVisible && parseFloat(String(account.amount || 0)) < 0
                ? "error.main"
                : "text.secondary"
            }
            sx={{ 
              minWidth: showDragHandle ? 90 : 100, 
              textAlign: "right", 
              lineHeight: showDragHandle ? 1.3 : undefined 
            }}
          >
            {valuesVisible
              ? formatCurrency(
                  parseFloat(String(account.amount || 0)),
                  account.currency
                )
              : "•••••"}
          </Typography>

          <IconButton
            size="small"
            onClick={(e) => onMenuOpen(e, account)}
            sx={{ flexShrink: 0, p: showDragHandle ? 0.25 : 0.5 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  );
}
